import { useEffect, useMemo } from "react";
import { IColumnProps } from "devextreme-react/data-grid";
import { IItemProps } from "devextreme-react/form";

import { PageProps, UpdateDomTitleForForm } from "@ezhou/core";

import { createFormulaEditCellComponent } from "../../components/formulaEditor";
import { FormulaContextKeys } from "../../model/formula";

import { BaseMappings } from "./BaseMappings";
import { accountingDateRuleMappingColumns, dummyAccountingDateRuleMappingRow } from "./model/metaData";

const FORMULA_FIELDS = ["accountingDateFormula"];
const DATE_FORMULA_FIELDS = ["accountingPeriodFormula", "fiscalYearFormula"];

export const AccountingDateRuleMappings = (pageProps: PageProps) => {
  useEffect(() => {
    UpdateDomTitleForForm(pageProps);
  });

  const logger = pageProps.loggerProvider.getLogger("AccountingDateRuleMappings");

  const httpClient = pageProps.appUtil.httpClients["eAccountingService"];

  // Render the formula editor through the column's `editCellComponent` so the DataGrid's editing
  // controller supplies the current value and receives changes. A form-item template would bypass
  // that binding and open with an empty editor for existing rows.
  const formulaEditCellComponent = useMemo(
    () =>
      createFormulaEditCellComponent({
        httpClient,
        contextKey: FormulaContextKeys.TessituraContext,
        rootObjects: ["TransactionInfo"],
      }),
    [httpClient],
  );

  // Period and fiscal year formulas can also reference the computed AccountingDate.
  const dateFormulaEditCellComponent = useMemo(
    () =>
      createFormulaEditCellComponent({
        httpClient,
        contextKey: FormulaContextKeys.TessituraContext,
        rootObjects: ["TransactionInfo", "AccountingDate"],
      }),
    [httpClient],
  );

  const columnConfigs = useMemo(() => {
    const configs = new Map<string, IColumnProps>(accountingDateRuleMappingColumns);
    FORMULA_FIELDS.forEach((field) => {
      const existing = configs.get(field) ?? { dataField: field };
      configs.set(field, { ...existing, editCellComponent: formulaEditCellComponent });
    });
    DATE_FORMULA_FIELDS.forEach((field) => {
      const existing = configs.get(field) ?? { dataField: field };
      configs.set(field, { ...existing, editCellComponent: dateFormulaEditCellComponent });
    });
    return configs;
  }, [formulaEditCellComponent, dateFormulaEditCellComponent]);

  const editorItemOptions = useMemo(() => {
    const options = new Map<string, IItemProps>();
    return options;
  }, []);

  return (
    <BaseMappings
      logger={logger}
      {...pageProps}
      baseUrl="accounting-date-rule-mappings"
      formTitle="Accounting Date Rule Mapping"
      dummyRow={dummyAccountingDateRuleMappingRow}
      columnConfigs={columnConfigs}
      additionalEditorItemOptions={editorItemOptions}
    />
  );
};
